import * as React from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { User, UserCheck, UserX } from "lucide-react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { axiosInstance } from "./../../utility/apiInstance";
import { useRole } from "../../components/MangeContextProvide";
import SearchInput from "../../common/SearchInput";
import WhatsAppDrawer from "./WhatsAppDrawer";
import TrackingThresholdDrawer from "../trackingThreshold/TrackingThresholdDrawer";
import StatCard from "../../common/StatCard";
import ClampText from "../../common/ClampText";
import ContactNumbers from "../../common/ContactNumbers";

const PAGE_SIZE = 10;

const menuOptions: Record<string, string> = {
  "1": "Air",
  "2": "Ocean",
  "3": "IDP",
};

// Fixed column widths (px). Sum drives the table width + horizontal scroll.
const COLS = {
  sno: 56,
  name: 190,
  pan: 120,
  gst: 155,
  country: 90,
  state: 110,
  address: 220,
  mobile: 150,
  email: 220,
  module: 110,
  status: 100,
  webhook: 130,
  actions: 110,
};
const TABLE_WIDTH = Object.values(COLS).reduce((a, b) => a + b, 0);

export default function OrganizationManage() {
  const navigate = useNavigate();
  const { role } = useRole() as any;

  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);

  const [totalOrganization, setTotalOrganization] = React.useState(0);
  const [activeOrganization, setActiveOrganization] = React.useState(0);
  const [inActiveOrganization, setInActiveOrganization] = React.useState(0);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [whatsAppDrawer, setWhatsAppDrawer] = React.useState(false);
  const [userId, setUserId] = React.useState("");

  const [thresholdDrawer, setThresholdDrawer] = React.useState(false);
  const [thresholdOrgId, setThresholdOrgId] = React.useState("");

  /* ── data ────────────────────────────────────────────────────────────── */

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const fetchOganizationList = async (search: string = "") => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/v1/organisation?search=${search}`);
      const fetchedRows = response?.data?.data || [];
      const data = fetchedRows.map((row: any) => ({
        ...row,
        key: row?._id || row?.id,
        id: row?._id || row?.id,
        module: row?.module,
        Organization_Name: row?.organizationName,
        PAN: row?.pan,
        GST: row?.gst,
        Office_Country: row?.officeCountry,
        Office_City: row?.officeCity,
        Address: row?.address,
        Mobile_Number: row?.mobileNumber,
        Admin_Email: row?.adminEmailId,
        numberOfLicenses: row?.numberOfLicenses,
        licensesconsumed: row?.consumeLiecence,
      }));
      setRows(data);
      if (response.data.statusCode === 401) handleLogout();
    } catch (err: any) {
      // keep previous rows on failure
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationCount = async () => {
    try {
      const response = await axiosInstance.get(`/api/v1/organisation/organization-count`);
      if (response?.data?.success) {
        const { totalOganizations, activeOganizations, inActiveOganizations } =
          response.data.data;
        setTotalOrganization(totalOganizations);
        setActiveOrganization(activeOganizations);
        setInActiveOrganization(inActiveOganizations);
      }
    } catch (error: any) {}
  };

  // debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  React.useEffect(() => {
    fetchOganizationList(debouncedSearch);
  }, [debouncedSearch]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  React.useEffect(() => {
    fetchOrganizationCount();
  }, []);

  /* ── actions ─────────────────────────────────────────────────────────── */

  const handleWhatsAppDrawer = (id: any) => {
    setWhatsAppDrawer(true);
    setUserId(id);
  };

  const handleThresholdDrawer = (id: any) => {
    setThresholdOrgId(id);
    setThresholdDrawer(true);
  };

  // Inline webhook toggle — reuses the organisation update endpoint.
  // NOTE: if you have a dedicated toggle endpoint, swap the axios call below.
  const handleToggleWebhook = async (row: any) => {
    const newVal = !row.isWebhookAllowed;

    // optimistic update
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, isWebhookAllowed: newVal } : r))
    );

    const sendData = {
      organizationName: row.Organization_Name,
      pan: row.PAN,
      gst: row.GST,
      officeCountry: row.Office_Country,
      officeCity: row.Office_City,
      address: row.Address,
      mobileNumber: row.Mobile_Number,
      adminEmailId: row.Admin_Email,
      isWebhookAllowed: newVal,
      module: row.module,
    };

    try {
      const response = await axiosInstance.patch(
        `/api/v1/organisation/update/${row.id}`,
        sendData
      );
      if (response.status === 200) {
        toast.success(response.data.message || "Webhook updated successfully");
      }
    } catch (err: any) {
      // revert on failure
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isWebhookAllowed: !newVal } : r))
      );
      const e = err?.response?.data?.error;
      toast.error(typeof e === "string" ? e : "Failed to update webhook.");
    }
  };

  const moduleLabel = (m: any) =>
    Array.isArray(m) ? m.map((v: string) => menuOptions[v]).filter(Boolean).join(", ") : "";

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  /* ── render ──────────────────────────────────────────────────────────── */

  return (
    <>
      {/* Stat cards */}
      {role === "admin" && (
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginTop: 8,
            marginBottom: 16,
          }}
        >
          <StatCard
            label="Total Organization"
            value={totalOrganization}
            color="#f59e0b"
            icon={<User size={22} color="#f59e0b" />}
          />
          <StatCard
            label="Active Organization"
            value={activeOrganization}
            color="#0f9d58"
            icon={<UserCheck size={22} color="#0f9d58" />}
          />
          <StatCard
            label="Inactive Organization"
            value={inActiveOrganization}
            color="#d92d20"
            icon={<UserX size={22} color="#d92d20" />}
          />
        </div>
      )}

      {/* Card: toolbar + table */}
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput placeholder="Search" onChange={setSearchQuery} width={340} />
          <div className="ctpl-spacer" />
        </div>

        <div className="ctpl-table-scroll">
          <table className="ctpl-table" style={{ width: TABLE_WIDTH, tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ width: COLS.sno }}>S.No.</th>
                <th style={{ width: COLS.name }}>Name</th>
                <th style={{ width: COLS.pan }}>PAN</th>
                <th style={{ width: COLS.gst }}>GST</th>
                <th style={{ width: COLS.country }}>Country</th>
                <th style={{ width: COLS.state }}>State</th>
                <th style={{ width: COLS.address }}>Address</th>
                <th style={{ width: COLS.mobile }}>Mobile No.</th>
                <th style={{ width: COLS.email }}>Email</th>
                <th style={{ width: COLS.module }}>Module</th>
                <th style={{ width: COLS.status }}>Status</th>
                <th style={{ width: COLS.webhook }}>Webhook</th>
                <th style={{ width: COLS.actions, textAlign: "right", paddingRight: 22 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="ctpl-empty-cell" colSpan={13}>
                    Loading organizations…
                  </td>
                </tr>
              ) : pagedRows.length === 0 ? (
                <tr>
                  <td className="ctpl-empty-cell" colSpan={13}>
                    No organizations found
                  </td>
                </tr>
              ) : (
                pagedRows.map((row: any, index: number) => (
                  <tr key={row.id}>
                    <td>{currentPage * PAGE_SIZE + index + 1}</td>
                    <td>
                      <ClampText text={row.Organization_Name} width="100%" className="ctpl-tmpl-name" />
                    </td>
                    <td>{row.PAN}</td>
                    <td>{row.GST}</td>
                    <td>{row.Office_Country}</td>
                    <td>{row.Office_City}</td>
                    <td>
                      <ClampText text={row.Address} width="100%" />
                    </td>
                    <td>
                      <ContactNumbers value={row.Mobile_Number} />
                    </td>
                    <td>
                      <ClampText text={row.Admin_Email} width="100%" lines={1} />
                    </td>
                    <td>{moduleLabel(row.module)}</td>
                    <td>
                      <span className={`ctpl-status-pill ${row.isActive ? "active" : "inactive"}`}>
                        <span className="ctpl-status-dot" />
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button
                          className={`ctpl-switch ${row.isWebhookAllowed ? "on" : ""}`}
                          onClick={() => handleToggleWebhook(row)}
                          aria-label="Toggle contact webhook"
                        />
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: row.isWebhookAllowed
                              ? "var(--ctpl-edit-dark)"
                              : "var(--ctpl-ink-soft)",
                          }}
                        >
                          {row.isWebhookAllowed ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="ctpl-row-actions">
                        <button
                          className="ctpl-action-circle"
                          style={{ background: "#25D366" }}
                          title="WhatsApp"
                          onClick={() => handleWhatsAppDrawer(row.id)}
                        >
                          <WhatsAppIcon sx={{ fontSize: 16 }} />
                        </button>
                        <button
                          className="ctpl-action-circle"
                          style={{ background: "#f5a524" }}
                          title="Tracking Threshold"
                          onClick={() => handleThresholdDrawer(row.id)}
                        >
                          <ScheduleIcon sx={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="ctpl-table-footer">
          <span>
            {rows.length} organization{rows.length === 1 ? "" : "s"}
          </span>
          <div className="ctpl-pagination">
            <button
              className="ctpl-page-btn"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            >
              ‹
            </button>
            <span>
              Page {currentPage + 1} of {pageCount}
            </span>
            <button
              className="ctpl-page-btn"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Drawers */}
      {whatsAppDrawer && (
        <WhatsAppDrawer
          whatsAppDrawer={whatsAppDrawer}
          onClose={() => {
            setWhatsAppDrawer(false);
            setUserId("");
          }}
          organizationId={userId}
        />
      )}

      {thresholdDrawer && (
        <TrackingThresholdDrawer
          open={thresholdDrawer}
          organizationId={thresholdOrgId}
          onClose={() => {
            setThresholdDrawer(false);
            setThresholdOrgId("");
          }}
        />
      )}
    </>
  );
}